import type { OrganizationRecipeExecutionSummary } from '@/services/api/recipes'
import type { AgentJobSnapshot } from '@/types/agentJob'

// Les huit états d'une ligne d'exécution (os-reorganisation-ecrans.md § 6).
// Un état n'est jamais déduit d'une donnée absente : une valeur inconnue du
// backend reste `unknown`.
export const EXECUTION_STATES = [
  'queued',
  'running',
  'awaiting_response',
  'awaiting_approval',
  'succeeded',
  'failed',
  'cancelled',
  'unknown',
] as const

export type ExecutionState = (typeof EXECUTION_STATES)[number]

export interface ExecutionCost {
  /** Montant dans l'unité de la devise (pas en unités mineures). */
  amount: number
  currency: string
}

export interface ExecutionTechnicalField {
  label: string
  value: string
}

export interface ExecutionRowModel {
  id: string
  title: string
  state: ExecutionState
  /** Statut tel que rendu par le backend, montré seulement dans le détail technique. */
  rawStatus?: string
  startedAt?: string | null
  endedAt?: string | null
  /** Durée mesurée par le runtime, distincte du temps écoulé entre deux dates. */
  runtimeMs?: number | null
  cost?: ExecutionCost | null
  /** Résultat ou erreur en une ligne ; absent quand le backend n'en donne pas. */
  outcome?: string | null
  outcomeTone?: 'neutral' | 'error' | 'warning'
  /** Clé i18n d'un motif (job bloqué) affiché comme résultat. */
  outcomeKey?: string | null
  technical: ExecutionTechnicalField[]
  raw?: unknown
}

export function workflowExecutionState(state?: string | null): ExecutionState {
  switch (state) {
    case 'pending':
    case 'draft':
    case 'queued':
      return 'queued'
    case 'running':
    case 'applying':
      return 'running'
    case 'waiting_for_form':
    case 'waiting_for_input':
      return 'awaiting_response'
    case 'waiting_approval':
    case 'waiting_for_approval':
      return 'awaiting_approval'
    case 'completed':
    case 'complete':
    case 'succeeded':
      return 'succeeded'
    case 'failed':
    case 'errored':
      return 'failed'
    case 'cancelled':
      return 'cancelled'
    default:
      return 'unknown'
  }
}

// Un job `blocked` attend une réponse humaine (relever un budget, vérifier un
// effet, confirmer un arrêt) : c'est l'état « réponse attendue », avec le motif
// en résultat. `expired` est un échec terminal du contrat AgentJob.
export function agentJobExecutionState(status?: string | null): ExecutionState {
  switch (status) {
    case 'queued':
      return 'queued'
    case 'leased':
    case 'running':
      return 'running'
    case 'blocked':
      return 'awaiting_response'
    case 'waiting_approval':
      return 'awaiting_approval'
    case 'completed':
    case 'succeeded':
      return 'succeeded'
    case 'failed':
    case 'expired':
      return 'failed'
    case 'cancelled':
      return 'cancelled'
    default:
      return 'unknown'
  }
}

export function isTerminalExecutionState(state: ExecutionState) {
  return state === 'succeeded' || state === 'failed' || state === 'cancelled'
}

function validDate(value?: string | null): Date | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** Date et heure avec le fuseau affiché (ex. « 11 oct. 2026, 14:05 UTC+2 »). */
export function formatZonedDateTime(value: string | null | undefined, locale?: string): string | null {
  const date = validDate(value)
  if (!date) return null
  return new Intl.DateTimeFormat(locale, {
    // dateStyle/timeStyle ne se combinent pas avec timeZoneName.
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(date)
}

export function formatDurationMs(value: number | null | undefined): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null
  if (value < 1000) return `${Math.round(value)} ms`
  const seconds = value / 1000
  if (seconds < 60) return `${seconds.toFixed(1)} s`
  const minutes = Math.floor(seconds / 60)
  const rest = Math.round(seconds - minutes * 60)
  if (minutes < 60) return `${minutes} min ${rest} s`
  const hours = Math.floor(minutes / 60)
  return `${hours} h ${minutes - hours * 60} min`
}

/** Temps écoulé entre deux dates connues ; jamais une horloge qui tourne. */
export function elapsedMs(start?: string | null, end?: string | null): number | null {
  const from = validDate(start)
  const to = validDate(end)
  if (!from || !to) return null
  const value = to.getTime() - from.getTime()
  return value >= 0 ? value : null
}

/** Ancienneté lisible à partir d'une date passée, relative à `now`. */
export function formatAge(value: string | null | undefined, locale?: string, now: Date = new Date()): string | null {
  const date = validDate(value)
  if (!date) return null
  const seconds = Math.max(0, Math.round((now.getTime() - date.getTime()) / 1000))
  const format = new Intl.RelativeTimeFormat(locale, { numeric: 'always', style: 'short' })
  if (seconds < 60) return format.format(-seconds, 'second')
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return format.format(-minutes, 'minute')
  const hours = Math.round(minutes / 60)
  if (hours < 48) return format.format(-hours, 'hour')
  return format.format(-Math.round(hours / 24), 'day')
}

export function formatCost(cost: ExecutionCost | null | undefined, locale?: string): string | null {
  if (!cost || !Number.isFinite(cost.amount)) return null
  const currency = cost.currency.trim().toUpperCase()
  if (!/^[A-Z]{3}$/.test(currency)) return null
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: cost.amount !== 0 && Math.abs(cost.amount) < 0.01 ? 6 : 2,
  }).format(cost.amount)
}

export function oneLine(value: string | null | undefined, max = 160): string | null {
  if (!value) return null
  const normalized = value.replace(/\s+/g, ' ').trim()
  if (!normalized) return null
  return normalized.length > max ? `${normalized.slice(0, max - 1)}…` : normalized
}

export function workflowExecutionRow(execution: OrganizationRecipeExecutionSummary): ExecutionRowModel {
  const state = workflowExecutionState(execution.state)
  const terminal = isTerminalExecutionState(state)
  const estimated = execution.usage_summary?.cost?.estimated_cost_usd
  return {
    id: execution.execution_id,
    title: execution.workflow_name || execution.workflow_id || execution.execution_id,
    state,
    rawStatus: execution.state,
    startedAt: execution.created_at,
    endedAt: terminal ? execution.updated_at : null,
    // Une durée nulle d'une exécution non terminée n'est pas une mesure.
    runtimeMs: terminal || execution.duration_ms > 0 ? execution.duration_ms : null,
    cost: typeof estimated === 'number' ? { amount: estimated, currency: 'USD' } : null,
    outcome: state === 'failed' || state === 'cancelled' ? oneLine(execution.error_message) : null,
    outcomeTone: state === 'failed' ? 'error' : 'neutral',
    technical: [
      { label: 'execution_id', value: execution.execution_id },
      { label: 'workflow_id', value: execution.workflow_id },
      ...(execution.workflow_version ? [{ label: 'workflow_version', value: execution.workflow_version }] : []),
      ...(execution.session_id ? [{ label: 'session_id', value: execution.session_id }] : []),
      ...(execution.trace_id ? [{ label: 'trace_id', value: execution.trace_id }] : []),
      { label: 'state', value: execution.state },
    ],
    raw: execution,
  }
}

export const JOB_BLOCKED_REASON_KEYS: Record<string, string> = {
  effect_outcome_unknown: 'agentJob.blockedReasonEffectOutcomeUnknown',
  permission_revoked: 'agentJob.blockedReasonPermissionRevoked',
  lease_lost: 'agentJob.blockedReasonLeaseLost',
  cost_unknown: 'agentJob.blockedReasonCostUnknown',
  budget_exceeded: 'agentJob.blockedReasonBudgetExceeded',
  validation_failed: 'agentJob.blockedReasonValidationFailed',
  runtime_stop_unknown: 'agentJob.stopUnknown',
}

/** Premier texte lisible d'un résultat, sans sérialiser d'objet. */
export function readableResultText(content?: Record<string, unknown> | null): string | null {
  if (!content) return null
  for (const value of Object.values(content)) {
    if (typeof value === 'string' && value.trim()) return oneLine(value)
  }
  return null
}

export function minorUnitsCost(minorUnits: number | string | null | undefined, currency?: string | null): ExecutionCost | null {
  const value = typeof minorUnits === 'string' ? Number(minorUnits) : minorUnits
  if (typeof value !== 'number' || !Number.isFinite(value) || !currency) return null
  return { amount: value / 100, currency }
}

export function agentJobExecutionRow(snapshot: AgentJobSnapshot): ExecutionRowModel {
  const job = snapshot.job
  const state = agentJobExecutionState(job.status)
  const blockedKey = job.blocked_reason ? JOB_BLOCKED_REASON_KEYS[job.blocked_reason] ?? null : null
  let outcome: string | null = null
  let outcomeTone: ExecutionRowModel['outcomeTone'] = 'neutral'
  if (job.status === 'blocked') {
    outcome = blockedKey ? null : oneLine(job.blocked_reason)
    outcomeTone = 'warning'
  } else if (state === 'failed') {
    outcome = oneLine(job.error?.message) ?? oneLine(job.error?.code)
    outcomeTone = 'error'
  } else if (state === 'succeeded') {
    outcome = readableResultText(snapshot.result?.content)
  }
  return {
    id: job.job_id,
    title: job.goal || job.runtime_ref?.workflow_id || job.job_id,
    state,
    rawStatus: job.status,
    startedAt: job.created_at,
    endedAt: snapshot.result?.completed_at ?? null,
    runtimeMs: snapshot.result?.metrics?.duration_ms ?? null,
    cost: minorUnitsCost(snapshot.cumulative_cost_minor_units, job.execution_policy?.currency),
    outcome,
    outcomeKey: job.status === 'blocked' ? blockedKey : null,
    outcomeTone,
    technical: [
      { label: 'job_id', value: job.job_id },
      ...(job.runtime_ref?.workflow_id ? [{ label: 'workflow_id', value: job.runtime_ref.workflow_id }] : []),
      ...(snapshot.recipe_execution_id ? [{ label: 'recipe_execution_id', value: snapshot.recipe_execution_id }] : []),
      ...(snapshot.lease?.attempt_id ? [{ label: 'attempt_id', value: snapshot.lease.attempt_id }] : []),
      ...(typeof snapshot.lease?.fencing_token === 'number' ? [{ label: 'fencing_token', value: String(snapshot.lease.fencing_token) }] : []),
      { label: 'status', value: job.status },
    ],
    raw: snapshot,
  }
}
